import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Mail, Phone, MapPin } from 'lucide-react';

export default function PrivacyPage() {
    return (
        <main className="main">
            {/* Hero Section */}
            <section className="hero" style={{
                minHeight: '300px',
                background: 'linear-gradient(193deg, rgb(242, 255, 168) 0%, rgb(255, 154, 255) 100%)'
            }}>
                <div className="container" style={{ textAlign: 'center' }}>
                    <Shield size={64} style={{ color: '#6418f3', marginBottom: '24px' }} />
                    <h1 style={{ fontSize: '48px', marginBottom: '24px' }}>Privacy Policy</h1>
                    <p style={{
                        fontSize: '14px',
                        marginTop: '16px',
                        color: 'var(--text-primary)'
                    }}>
                        Last Updated: December 29, 2025
                    </p>
                </div>
            </section>

            {/* Content Section */}
            <section style={{ padding: '80px 0' }}>
                <div className="container" style={{ maxWidth: '900px', margin: '0 auto' }}>

                    {/* Introduction */}
                    <div style={{ marginBottom: '48px' }}>
                        <h2 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '16px', color: 'var(--text-primary)' }}>
                            1. Introduction
                        </h2>
                        <p style={{ color: 'var(--text-primary)', lineHeight: '1.7', fontSize: '15px' }}>
                            ATAM Connect ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information when you use our Platform.
                        </p>
                    </div>

                    {/* Information We Collect */}
                    <div style={{ marginBottom: '48px' }}>
                        <h2 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '16px', color: 'var(--text-primary)' }}>
                            2. Information We Collect
                        </h2>

                        <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px', marginTop: '24px' }}>
                            2.1 Information You Provide
                        </h3>
                        <p style={{ color: 'var(--text-primary)', lineHeight: '1.7', fontSize: '15px', marginBottom: '12px' }}>
                            When you use our contact form or subscribe to updates, we may collect:
                        </p>
                        <ul style={{ color: 'var(--text-primary)', lineHeight: '1.7', fontSize: '15px', paddingLeft: '24px', marginBottom: '20px' }}>
                            <li>Name and email address</li>
                            <li>Contact information (phone number, if provided)</li>
                            <li>Message content and inquiry details</li>
                        </ul>

                        <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px', marginTop: '24px' }}>
                            2.2 Automatically Collected Information
                        </h3>
                        <p style={{ color: 'var(--text-primary)', lineHeight: '1.7', fontSize: '15px', marginBottom: '12px' }}>
                            We automatically collect certain information when you visit the Platform:
                        </p>
                        <ul style={{ color: 'var(--text-primary)', lineHeight: '1.7', fontSize: '15px', paddingLeft: '24px', marginBottom: '20px' }}>
                            <li>Browser type and version</li>
                            <li>Device information and operating system</li>
                            <li>IP address and location data</li>
                            <li>Pages visited and time spent on pages</li>
                            <li>Referring website addresses</li>
                        </ul>

                        <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px', marginTop: '24px' }}>
                            2.3 Cookies and Tracking Technologies
                        </h3>
                        <p style={{ color: 'var(--text-primary)', lineHeight: '1.7', fontSize: '15px' }}>
                            We use cookies and similar tracking technologies to enhance your experience. You can control cookie settings through your browser preferences.
                        </p>
                    </div>

                    {/* How We Use Your Information */}
                    <div style={{ marginBottom: '48px' }}>
                        <h2 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '16px', color: 'var(--text-primary)' }}>
                            3. How We Use Your Information
                        </h2>
                        <p style={{ color: 'var(--text-primary)', lineHeight: '1.7', fontSize: '15px', marginBottom: '12px' }}>
                            We use the collected information for the following purposes:
                        </p>
                        <ul style={{ color: 'var(--text-primary)', lineHeight: '1.7', fontSize: '15px', paddingLeft: '24px' }}>
                            <li>To provide and maintain our Platform</li>
                            <li>To respond to your inquiries and support requests</li>
                            <li>To send event updates and newsletters (with your consent)</li>
                            <li>To improve and personalize user experience</li>
                            <li>To analyze Platform usage and trends</li>
                            <li>To detect and prevent fraud or abuse</li>
                            <li>To comply with legal obligations</li>
                        </ul>
                    </div>

                    {/* Information Sharing */}
                    <div style={{ marginBottom: '48px' }}>
                        <h2 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '16px', color: 'var(--text-primary)' }}>
                            4. Information Sharing
                        </h2>
                        <p style={{ color: 'var(--text-primary)', lineHeight: '1.7', fontSize: '15px', marginBottom: '12px' }}>
                            We do not sell your personal information. We may share information in the following circumstances:
                        </p>
                        <ul style={{ color: 'var(--text-primary)', lineHeight: '1.7', fontSize: '15px', paddingLeft: '24px' }}>
                            <li><strong>With Event Organizers:</strong> When you express interest in an event</li>
                            <li><strong>With Service Providers:</strong> Third parties who assist in Platform operations</li>
                            <li><strong>For Legal Requirements:</strong> When required by law or to protect rights</li>
                            <li><strong>With Your Consent:</strong> When you explicitly authorize sharing</li>
                        </ul>
                    </div>

                    {/* Data Security */}
                    <div style={{ marginBottom: '48px' }}>
                        <h2 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '16px', color: 'var(--text-primary)' }}>
                            5. Data Security
                        </h2>
                        <p style={{ color: 'var(--text-primary)', lineHeight: '1.7', fontSize: '15px' }}>
                            We implement appropriate technical and organizational measures to protect your personal information. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
                        </p>
                    </div>

                    {/* Your Rights */}
                    <div style={{ marginBottom: '48px' }}>
                        <h2 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '16px', color: 'var(--text-primary)' }}>
                            6. Your Rights
                        </h2>
                        <p style={{ color: 'var(--text-primary)', lineHeight: '1.7', fontSize: '15px', marginBottom: '12px' }}>
                            You have the following rights regarding your personal information:
                        </p>
                        <ul style={{ color: 'var(--text-primary)', lineHeight: '1.7', fontSize: '15px', paddingLeft: '24px' }}>
                            <li><strong>Access:</strong> Request a copy of your personal data</li>
                            <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                            <li><strong>Deletion:</strong> Request deletion of your personal data</li>
                            <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications</li>
                            <li><strong>Data Portability:</strong> Receive your data in a structured format</li>
                        </ul>
                    </div>

                    {/* Third-Party Services */}
                    <div style={{ marginBottom: '48px' }}>
                        <h2 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '16px', color: 'var(--text-primary)' }}>
                            7. Third-Party Services
                        </h2>
                        <p style={{ color: 'var(--text-primary)', lineHeight: '1.7', fontSize: '15px' }}>
                            Our Platform uses third-party services including Firebase (Google), Google Maps, and BookMyShow for ticketing. These services have their own privacy policies, and we encourage you to review them.
                        </p>
                    </div>

                    {/* Children's Privacy */}
                    <div style={{ marginBottom: '48px' }}>
                        <h2 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '16px', color: 'var(--text-primary)' }}>
                            8. Children's Privacy
                        </h2>
                        <p style={{ color: 'var(--text-primary)', lineHeight: '1.7', fontSize: '15px' }}>
                            Our Platform is not intended for children under 13. We do not knowingly collect personal information from children. If you are a parent or guardian and believe your child has provided us with personal information, please contact us.
                        </p>
                    </div>

                    {/* Changes to Privacy Policy */}
                    <div style={{ marginBottom: '48px' }}>
                        <h2 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '16px', color: 'var(--text-primary)' }}>
                            9. Changes to Privacy Policy
                        </h2>
                        <p style={{ color: 'var(--text-primary)', lineHeight: '1.7', fontSize: '15px' }}>
                            We may update this Privacy Policy periodically. Changes will be posted on this page with an updated "Last Updated" date. Continued use of the Platform constitutes acceptance of the updated policy.
                        </p>
                    </div>

                    {/* Contact Us */}
                    <div style={{ marginBottom: '48px' }}>
                        <h2 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '16px', color: 'var(--text-primary)' }}>
                            10. Contact Us
                        </h2>
                        <p style={{ color: 'var(--text-primary)', lineHeight: '1.7', fontSize: '15px', marginBottom: '16px' }}>
                            For questions about this Privacy Policy or to exercise your rights, contact us at:
                        </p>
                        <div style={{
                            background: 'var(--bg-secondary)',
                            padding: '24px',
                            borderRadius: '12px',
                            border: '1px solid var(--border)'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                                <Mail size={20} style={{ color: 'var(--text-primary)' }} />
                                <a href="mailto:admin@atam.sg" style={{ color: 'var(--text-primary)', textDecoration: 'none' }}>
                                    admin@atam.sg
                                </a>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                                <Phone size={20} style={{ color: 'var(--text-primary)' }} />
                                <a href="tel:+6589327048" style={{ color: 'var(--text-primary)', textDecoration: 'none' }}>
                                    +65 8932 7048
                                </a>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                <MapPin size={20} style={{ color: 'var(--text-primary)', marginTop: '2px' }} />
                                <span style={{ color: 'var(--text-primary)' }}>
                                    1 Ang Mo Kio Industrial Park 2A, #06-06 AMK Tech 1, Singapore 568049
                                </span>
                            </div>
                        </div>
                    </div>

                </div>
            </section>

            {/* Related Links */}
            <section style={{
                padding: '48px 0',
                background: 'var(--bg-secondary)',
                borderTop: '1px solid var(--border)'
            }}>
                <div className="container" style={{ maxWidth: '700px', margin: '0 auto', textAlign: 'center' }}>
                    <p style={{ color: 'var(--text-primary)', marginBottom: '16px' }}>
                        Related Documents
                    </p>
                    <div style={{ display: 'flex', gap: '24px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Link to="/terms" className="btn btn-ghost">
                            Terms & Conditions
                        </Link>
                        <Link to="/about" className="btn btn-ghost">
                            About Us
                        </Link>
                        <Link to="/contact" className="btn btn-ghost">
                            Contact Us
                        </Link>
                    </div>
                </div>
            </section>
        </main>
    );
}
